import React, {type FC, useState} from 'react';
import {useLocation} from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import IconButton from '@mui/material/IconButton';
import {Pencil, Trash2} from 'lucide-react';
import Button from '@mui/material/Button';
import cloneDeep from 'lodash/cloneDeep';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

import { Flex, FlexColumn } from 'src/components/flex';
import SearchInput from 'src/pages/landlord/conversations/components/header/ConversationsSearchInput';
import FilterStatus from 'src/pages/landlord/conversations/components/header/ConversationsFilterStatus';
import FilterButton from 'src/pages/landlord/conversations/components/header/ConversationsFilterButton';
import {useConversationsPageContext} from 'src/pages/landlord/conversations/context';
import {useCreateInbox} from 'src/services/api/inboxes/use-create-inbox';
import {useUpdateInbox} from 'src/services/api/inboxes/use-update-inbox';
import {useDeleteInbox} from 'src/services/api/inboxes/use-delete-inbox';
import {InboxCreateEditForm, type SubmitPayload} from 'src/pages/landlord/inboxes/InboxCreateEditForm';
import ConfirmationDialog from 'src/components/confirmation-dialog';
import {usePrimaryInbox} from 'src/services/api/inboxes/use-primary-inbox';
import { INITIAL_FILTERS } from 'src/pages/landlord/conversations/context/Provider';
import type {TConversationsFilter} from 'src/services/api/messages/use-conversations-search';
import useCurrentInbox from 'src/services/api/inboxes/use-current-inbox';

type ConversationsHeaderProps = {
    searchScope: 'inbox' | 'all';
}

type InboxEditedItem = {id?: number, name?: string, is_private?: boolean, filters?: TConversationsFilter};

const ConversationsInboxHeader = () => {
    const { isDefaultInbox, inbox, isOwner} = useCurrentInbox();
    const {
        filter: searchFilter,
    } = useConversationsPageContext();
    const [isCreateOrUpdateInboxModalOpen, setIsCreateOrUpdateInboxModalOpen] = useState(false);
    const [editedItem, setEditedItem] = useState<InboxEditedItem | undefined>(undefined);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const { goToInboxesList } = usePrimaryInbox();

    const { mutateAsync: createInbox, isLoading: createLoading } = useCreateInbox();
    const { mutateAsync: updateInbox, isLoading: updateLoading } = useUpdateInbox(inbox?.id);
    const { mutateAsync: deleteInbox, isLoading: deleteLoading } = useDeleteInbox();

    const isFilterEdited = isDefaultInbox ? !isEqual(searchFilter, INITIAL_FILTERS) : !isEqual(searchFilter, inbox?.filters);

    const isSubmitting = createLoading || updateLoading || deleteLoading;


    const submitCreateOrUpdate = (values: SubmitPayload) => {
        setIsCreateOrUpdateInboxModalOpen(false);
        if (editedItem?.id) {
            updateInbox(values);
        } else {
            createInbox(values);
        }
        setEditedItem(undefined);
    };

    const handleCloseModal = () => {
        setIsCreateOrUpdateInboxModalOpen(false);
        setEditedItem(undefined);
    };

    const handleDuplicateInbox = () => {
        setEditedItem({
            filters: cloneDeep(searchFilter)
        });
        setIsCreateOrUpdateInboxModalOpen(true);
    };

    const handleSaveOwnerChanges = () => {
        if (isOwner && !isDefaultInbox && inbox) {
            updateInbox({
                name: inbox.name ?? '',
                filters: searchFilter
            });
        }
    };

    const handleDeleteInbox = async () => {
        if (isDefaultInbox || !inbox) {
            return;
        }
        setIsDeleteConfirmOpen(false);
        deleteInbox(inbox.id);
        goToInboxesList();
    };

    const handleOpenEditModal = () => {
        if (!inbox) {
            return;
        }
        setEditedItem({
            name: inbox.name,
            is_private: inbox.is_private,
            filters: cloneDeep(inbox.filters),
            id: inbox.id,
        });
        setIsCreateOrUpdateInboxModalOpen(true);
    };

    return (
        <>
            <Flex justifyContent="space-between" mobileFlexDirection="column">
                <Flex alignItems="center" gap={2}>
                    <Typography variant="h2" fontSize={24} fontWeight={600} lineHeight={1} py={1}>{inbox?.name ?? 'Team Inbox'}</Typography>
                    {isFilterEdited && !isDefaultInbox && (
                        <Chip label="Unsaved Changes"
                            color="info"
                            size="small"
                        />
                    )}
                </Flex>
                <Flex alignItems="center" gap={1}>
                    {isFilterEdited &&
                        <Button variant="outlined" onClick={handleDuplicateInbox}>Save as New Inbox</Button>}
                    {isFilterEdited && isOwner &&
                        <Button variant="contained" onClick={handleSaveOwnerChanges}>Save Changes</Button>}
                    {isOwner && (<>
                        <IconButton onClick={handleOpenEditModal} sx={{
                            border: '1px solid #10b981',
                            borderRadius: '20%',
                            padding: '6px',
                        }}
                        >
                            <Pencil size="20" color="#10b981" />
                        </IconButton>
                        <IconButton onClick={() => setIsDeleteConfirmOpen(true)} sx={{
                            border: '1px solid red',
                            borderRadius: '20%',
                            padding: '6px',
                        }}
                        >
                            <Trash2 color="red" size="20" />
                        </IconButton>
                    </>)}
                </Flex>
            </Flex>
            {isCreateOrUpdateInboxModalOpen && (
                <InboxCreateEditForm
                    onSubmit={submitCreateOrUpdate}
                    handleClose={handleCloseModal}
                    initialValues={editedItem}
                    isLoading={isSubmitting}
                />
            )}
            {isDeleteConfirmOpen && (
                <ConfirmationDialog
                    title={`Are you sure you want to delete "${inbox?.name}" ?`}
                    content="This action cannot be undone."
                    onConfirm={handleDeleteInbox}
                    onClose={() => setIsDeleteConfirmOpen(false)}
                    confirmButtonColor="error"
                    confirmButtonText="Delete"
                />
            )}
        </>
    );
};

const ConversationsHeader: FC<ConversationsHeaderProps> = ({ searchScope })=> {
    const { pathname } = useLocation();
    const isInboxesPage = pathname.startsWith('/landlord/inner/inboxes');
    return (
        <>
            <FlexColumn p={2}>
                {isInboxesPage && <ConversationsInboxHeader />}
                <Flex alignItems="center">
                    <SearchInput searchScope={searchScope} />
                    <FilterButton />
                </Flex>

                <Flex
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{
                        '& .MuiChip-root': { maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' },
                        '& .MuiSvgIcon-root': { ml: 1, fontSize: 20 }
                    }}
                >
                    <FilterStatus />
                </Flex>
            </FlexColumn>
        </>
    );
};

export default ConversationsHeader;
